import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { openCommissionEditModal } from '@wix/da-shared-react/pkg/redux/commissions/actionCreators';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { getProfileOwnerUser } from '../../../../../selectors/users';
import ThumbStatusIndicator from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicator';
import type { ThumbStatusTypes } from '@wix/da-ds/pkg/thumbs/types';

import s from './ShopOwnerEmptyState.scss';

const ILLUSTRATION_BASE_URL =
  'https://st.deviantart.net/eclipse/illustrations/empty-shop';

interface SectionConfig {
  key: string;
  titleKey: string;
  subtitleKey: string;
  illustrationUrl: string;
  reversed: boolean;
  cta: 'upgradeToCore' | 'createCommission';
  coreCtaKey: string;
  coreClickInfo: string;
  coreAction:
    | 'studio'
    | 'createCommission'
    | 'createOriginal'
    | 'createPremiumGallery';
  referralType: string;
  artByUsername: string;
  statusType: ThumbStatusTypes;
}

const SECTIONS: SectionConfig[] = [
  {
    key: 'exclusives',
    titleKey: 'pages.shop.ownerEmptyState.exclusives.title',
    subtitleKey: 'pages.shop.ownerEmptyState.exclusives.subtitle',
    illustrationUrl: `${ILLUSTRATION_BASE_URL}/exclusives.png`,
    reversed: false,
    cta: 'upgradeToCore',
    coreCtaKey: 'pages.shop.ownerEmptyState.exclusives.cta',
    coreClickInfo: 'create_exclusive',
    coreAction: 'studio',
    referralType: ReferralType.SHOP_ADOPTABLES,
    artByUsername: 'Miizue',
    statusType: 'adoptable',
  },
  {
    key: 'premiumGalleries',
    titleKey: 'pages.shop.ownerEmptyState.premiumGalleries.title',
    subtitleKey: 'pages.shop.ownerEmptyState.premiumGalleries.subtitle',
    illustrationUrl: `${ILLUSTRATION_BASE_URL}/premium-galleries.png`,
    reversed: true,
    cta: 'upgradeToCore',
    coreCtaKey: 'pages.shop.ownerEmptyState.premiumGalleries.cta',
    coreClickInfo: 'create_premium_gallery',
    coreAction: 'createPremiumGallery',
    referralType: ReferralType.SHOP_PREMIUM_DOWNLOADS,
    artByUsername: 'ERA7',
    statusType: 'premium-folder',
  },
  {
    key: 'premiumDownloads',
    titleKey: 'pages.shop.ownerEmptyState.premiumDownloads.title',
    subtitleKey: 'pages.shop.ownerEmptyState.premiumDownloads.subtitle',
    illustrationUrl: `${ILLUSTRATION_BASE_URL}/premium-downloads.png`,
    reversed: false,
    cta: 'upgradeToCore',
    coreCtaKey: 'pages.shop.ownerEmptyState.premiumDownloads.cta',
    coreClickInfo: 'create_premium_download',
    coreAction: 'studio',
    referralType: ReferralType.SHOP_PREMIUM_DOWNLOADS,
    artByUsername: 'Anastasia-berry',
    statusType: 'pcp',
  },
  {
    key: 'originals',
    titleKey: 'pages.shop.ownerEmptyState.originals.title',
    subtitleKey: 'pages.shop.ownerEmptyState.originals.subtitle',
    illustrationUrl: `${ILLUSTRATION_BASE_URL}/originals.png`,
    reversed: true,
    cta: 'upgradeToCore',
    coreCtaKey: 'pages.shop.ownerEmptyState.originals.cta',
    coreClickInfo: 'offer_originals',
    coreAction: 'createOriginal',
    referralType: ReferralType.SHOP_ADOPTABLES,
    artByUsername: 'Sakurai-Outfit-Adopt',
    statusType: 'commission',
  },
  {
    key: 'commissions',
    titleKey: 'pages.shop.ownerEmptyState.commissions.title',
    subtitleKey: 'pages.shop.ownerEmptyState.commissions.subtitle',
    illustrationUrl: `${ILLUSTRATION_BASE_URL}/commissions.png`,
    reversed: false,
    cta: 'createCommission',
    coreCtaKey: 'pages.shop.ownerEmptyState.createCommission',
    coreClickInfo: 'create_commission',
    coreAction: 'createCommission',
    referralType: '',
    artByUsername: 'Precia-T',
    statusType: 'commission',
  },
];

export interface Props {
  className?: string;
}

const ShopOwnerEmptyState: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasCoreMembership = useSelector(getCurrentUserHasCore);
  const profileOwnerUser = useSelector(getProfileOwnerUser);

  const handleCreateCommission = () => {
    dispatch(openCommissionEditModal({ deviationId: undefined }));
  };

  const handleNavigateToStudio = () => {
    if (typeof window !== 'undefined') {
      window.location.assign(Url.studio({ openSubmitForm: true }));
    }
  };

  const handleCreateOriginal = () => {
    if (typeof window !== 'undefined') {
      window.location.assign(Url.studio({ openCreateForm: true }));
    }
  };

  const handleCreatePremiumGallery = () => {
    if (typeof window !== 'undefined' && profileOwnerUser) {
      const path = Url.userShopPath(
        profileOwnerUser.username,
        USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS
      );
      window.location.assign(`${path}?new_premium_folder`);
    }
  };

  const getCoreActionHandler = (section: SectionConfig) => {
    switch (section.coreAction) {
      case 'createCommission':
        return handleCreateCommission;
      case 'createOriginal':
        return handleCreateOriginal;
      case 'createPremiumGallery':
        return handleCreatePremiumGallery;
      case 'studio':
      default:
        return handleNavigateToStudio;
    }
  };

  const renderCta = (section: SectionConfig) => {
    if (hasCoreMembership || section.cta === 'createCommission') {
      return (
        <BrandedButton
          size="large"
          variant="brand-green"
          onClick={getCoreActionHandler(section)}
          biData={BiData<DefaultBiEvent>({
            evid: 2,
            click_info: section.coreClickInfo,
            sectionname: section.key,
          })}
        >
          {t(section.coreCtaKey)}
        </BrandedButton>
      );
    }

    return (
      <BuyCoreBrandedButton
        size="large"
        biData={
          {
            referral_info: section.referralType,
          } as any
        }
      >
        {t('common.upgradeToCoreCTA')}
      </BuyCoreBrandedButton>
    );
  };

  return (
    <div className={classnames(s['root'], className)}>
      {SECTIONS.map(section => (
        <div
          key={section.key}
          className={classnames(
            s['section'],
            section.reversed && s['section-reversed']
          )}
        >
          <div className={s['text-content']}>
            <div className={s['header']}>
              <div className={s['title-row']}>
                <ThumbStatusIndicator
                  type={section.statusType}
                  size="large"
                  className={s['status-indicator']}
                />
                <span className={s['title']}>{t(section.titleKey)}</span>
              </div>
              <p className={s['subtitle']}>{t(section.subtitleKey)}</p>
            </div>
            <div className={s['cta-wrap']}>{renderCta(section)}</div>
          </div>
          <div className={s['illustration-wrap']}>
            <img
              className={s['illustration-img']}
              src={section.illustrationUrl}
              alt={t(section.titleKey)}
            />
            <span className={s['art-by']}>
              {t('pages.shop.ownerEmptyState.artBy')}{' '}
              <a
                className={s['art-by-link']}
                href={Url.userLink(section.artByUsername)}
              >
                {section.artByUsername}
              </a>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

ShopOwnerEmptyState.displayName = 'ShopOwnerEmptyState';

export default ShopOwnerEmptyState;
